import React, { useEffect, useMemo, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import ReactFlow, {
  Background,
  Controls,
  Edge,
  getSmoothStepPath,
  Handle,
  MiniMap,
  Node,
  NodeProps,
  Position,
  ReactFlowProvider,
  useNodesState,
  useReactFlow,
  type EdgeProps,
} from 'reactflow'
import 'reactflow/dist/style.css'
import {
  DocumentWorkflowDefinition,
  documentWorkflowDefinitionService,
  DocumentWorkflowDefinitionStep,
  DocumentWorkflowDefinitionStepAction,
  DocumentWorkflowDefinitionStepActionBranch,
} from '../../services/document-workflow-definition.service'
import { customFieldService } from '../../services/custom-field.service'
import { documentService } from '../../services/document.service'
import { workflowStatusService } from '../../services/workflow-status.service'
import { userService } from '../../services/user.service'
import { CustomField, WorkflowStatus } from '../../types'
import { TFunction } from 'i18next'
import './DocumentWorkflowDefinitionVisualizationDialog.scss'

const nodeWidth = 220
const nodeHeight = 72
const verticalSpacing = 160
const horizontalSpacing = 280
/** Boje ivica: po imenu akcije (accept/reject) ili ciklus po indeksu */
const EDGE_COLOR_ACCEPT = '#059669'
const EDGE_COLOR_REJECT = '#dc2626'
const EDGE_COLORS = ['#0ea5e9', EDGE_COLOR_ACCEPT, EDGE_COLOR_REJECT, '#7c3aed', '#d97706']

type ConditionFieldMeta = {
  name: string
  data_type: string
  selectOptionLabelById?: Record<string, string>
}

const OPERATOR_SYMBOLS: Record<string, string> = {
  gte: '≥',
  gt: '>',
  lte: '≤',
  lt: '<',
  exact: '=',
  icontains: '∋',
  contains: '∋',
  in: '∈',
}

const CONDITION_DATE_FORMAT = new Intl.DateTimeFormat(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' })

function formatDateValue(raw: string): string {
  const parsed = new Date(raw)
  if (Number.isNaN(parsed.getTime())) return raw
  return CONDITION_DATE_FORMAT.format(parsed)
}

function formatConditionValueScalar(raw: unknown, field: ConditionFieldMeta | undefined, t: TFunction): string {
  if (raw == null) return ''
  const rawStr = String(raw)
  if (!field) return rawStr
  switch (field.data_type) {
    case 'boolean':
      if (rawStr.toLowerCase() === 'true') return t('yes')
      if (rawStr.toLowerCase() === 'false') return t('no')
      return rawStr
    case 'select':
      return field.selectOptionLabelById?.[rawStr] ?? rawStr
    case 'date':
      return formatDateValue(rawStr)
    default:
      return rawStr
  }
}

function formatConditionValue(
  raw: unknown,
  field: ConditionFieldMeta | undefined,
  documentTitleById: Record<number, string>,
  t: TFunction,
): string {
  if (field?.data_type === 'documentlink') {
    const ids = Array.isArray(raw) ? raw : [raw]
    return ids
      .map((id) => {
        const numericId = typeof id === 'number' ? id : Number(id)
        if (!Number.isFinite(numericId)) return String(id)
        const title = documentTitleById[numericId]
        return title ? `"${title}"` : `#${numericId}`
      })
      .join(', ')
  }
  if (Array.isArray(raw)) {
    return raw.map((v) => formatConditionValueScalar(v, field, t)).join(', ')
  }
  return formatConditionValueScalar(raw, field, t)
}

function formatAtomLabel(
  fieldId: unknown,
  operator: unknown,
  value: unknown,
  fieldMetaById: Record<number, ConditionFieldMeta>,
  documentTitleById: Record<number, string>,
  t: TFunction,
): string {
  const meta = typeof fieldId === 'number' ? fieldMetaById[fieldId] : undefined
  const name = meta?.name ?? `#${fieldId}`
  const opKey = String(operator)

  // `exists` / `isnull` prefer natural-language rendering over an operator symbol.
  if (opKey === 'exists' || opKey === 'isnull') {
    const isTrue = String(value).toLowerCase() === 'true'
    const key = opKey === 'exists'
      ? isTrue ? 'exists_populated' : 'exists_empty'
      : isTrue ? 'isnull_yes' : 'isnull_no'
    return `${name} — ${t(`document_workflow.condition_render.${key}`)}`
  }

  const symbol = OPERATOR_SYMBOLS[opKey] ?? opKey
  const rendered = formatConditionValue(value, meta, documentTitleById, t)
  return `${name} ${symbol} ${rendered}`
}

/** Prevede JSON uslov iz CustomFieldQueryParser formata u čitljiv opis (root operator + do tri atoma po grupi, ostatak "…"). */
function summarizeConditionQuery(
  conditionQuery: string,
  fieldMetaById: Record<number, ConditionFieldMeta>,
  documentTitleById: Record<number, string>,
  t: TFunction,
): string {
  if (!conditionQuery?.trim()) return ''
  let parsed: unknown
  try {
    parsed = JSON.parse(conditionQuery)
  } catch {
    return conditionQuery.length > 40 ? `${conditionQuery.slice(0, 37)}…` : conditionQuery
  }
  const render = (node: unknown, depth: number): string => {
    if (!Array.isArray(node)) return ''
    if (node.length === 3) {
      return formatAtomLabel(node[0], node[1], node[2], fieldMetaById, documentTitleById, t)
    }
    if (node.length === 2 && typeof node[0] === 'string' && Array.isArray(node[1])) {
      const [op, elems] = node as [string, unknown[]]
      const parts = elems.map((el) => render(el, depth + 1)).filter(Boolean)
      if (!parts.length) return ''
      const lop = op.toLowerCase()
      const glueLabel = lop === 'and'
        ? t('document_workflow.condition_render.and')
        : lop === 'or'
          ? t('document_workflow.condition_render.or')
          : op
      const glue = ` ${glueLabel} `
      const joined = parts.slice(0, 3).join(glue) + (parts.length > 3 ? `${glue}…` : '')
      return depth === 0 ? joined : `(${joined})`
    }
    return ''
  }
  const label = render(parsed, 0).trim()
  return label || (conditionQuery.length > 40 ? `${conditionQuery.slice(0, 37)}…` : conditionQuery)
}

/** Pređi sve entry_conditions definicije i skupi ID-jeve dokumenata korišćene u atomima nad DocumentLink poljima. */
function collectDocumentLinkIds(
  steps: DocumentWorkflowDefinitionStep[],
  fieldMetaById: Record<number, ConditionFieldMeta>,
): number[] {
  const ids = new Set<number>()
  const visit = (node: unknown) => {
    if (!Array.isArray(node)) return
    if (node.length === 3) {
      const [fieldId, , value] = node
      const meta = typeof fieldId === 'number' ? fieldMetaById[fieldId] : undefined
      if (meta?.data_type !== 'documentlink') return
      const raw = Array.isArray(value) ? value : [value]
      for (const v of raw) {
        const n = typeof v === 'number' ? v : Number(v)
        if (Number.isFinite(n)) ids.add(n)
      }
      return
    }
    if (node.length === 2 && Array.isArray(node[1])) {
      for (const child of node[1] as unknown[]) visit(child)
    }
  }
  for (const step of steps) {
    for (const cond of step.entry_conditions ?? []) {
      if (!cond.condition_query?.trim()) continue
      try {
        visit(JSON.parse(cond.condition_query))
      } catch {
        // Malformed query — summarizer se pobrine za render.
      }
    }
    for (const action of step.actions ?? []) {
      for (const branch of action.branches ?? []) {
        if (!branch.condition_query?.trim()) continue
        try {
          visit(JSON.parse(branch.condition_query))
        } catch {
          // Malformed query — ignoriši.
        }
      }
    }
  }
  return Array.from(ids)
}

function getEdgeColorForAction(actionName: string | undefined, actionIdx: number): string {
  const name = (actionName ?? '').toLowerCase().trim()
  if (name.includes('accept') || name.includes('prihvati')) return EDGE_COLOR_ACCEPT
  if (name.includes('reject') || name.includes('odbij')) return EDGE_COLOR_REJECT
  return EDGE_COLORS[actionIdx % EDGE_COLORS.length]
}

/** Custom step node – jedan ulazni handle na vrhu, više izlaznih (out-0, out-1, …) da se sve ivice crtaju */
const DocumentWorkflowStepNode: React.FC<
  NodeProps<{
    stepLabel: string
    statusName: string
    outputCount: number
    assigneeSummary?: string
    entryConditionCount?: number
    entryConditionSummary?: string
    entryConditionLabel?: string
  }>
> = ({ data }) => {
  const { stepLabel, statusName, outputCount, assigneeSummary, entryConditionCount, entryConditionSummary, entryConditionLabel } = data
  const hasOutputs = outputCount > 0
  const hasEntryConditions = (entryConditionCount ?? 0) > 0
  return (
    <div className="doc-workflow-viz-node-content doc-workflow-viz-step-node">
      <Handle type="target" position={Position.Top} id="in" style={{ left: '50%', transform: 'translateX(-50%)' }} />
      <div className="doc-workflow-viz-node-title">{stepLabel}</div>
      <div className="doc-workflow-viz-node-details">{statusName}</div>
      {hasEntryConditions && (
        <div
          className="doc-workflow-viz-node-details doc-workflow-viz-node-entry-conditions"
          title={entryConditionSummary || ''}
        >
          <i className="bi bi-shield-lock me-1" />
          {entryConditionLabel}
        </div>
      )}
      {assigneeSummary && (
        <div className="doc-workflow-viz-node-details doc-workflow-viz-node-assignees">{assigneeSummary}</div>
      )}
      {hasOutputs &&
        Array.from({ length: outputCount }).map((_, i) => (
          <Handle
            key={i}
            type="source"
            position={Position.Bottom}
            id={`out-${i}`}
            style={{
              left: outputCount === 1 ? '50%' : `${((i + 1) / (outputCount + 1)) * 100}%`,
              transform: 'translateX(-50%)',
            }}
          />
        ))}
    </div>
  )
}

/** Zaobilazna udaljenost u px – linija ide u stranu pa dole da ne prelazi preko koraka */
const BYPASS_OFFSET = 160

/** Custom edge – path sa markerEnd (strelica) i label; smer akcije uvek vidljiv */
const DocWorkflowVizEdge: React.FC<EdgeProps> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  style,
  label,
}) => {
  const edgeData = data as { pathOffsetX?: number; stroke?: string } | undefined
  const pathOffsetX = edgeData?.pathOffsetX
  const stroke = edgeData?.stroke ?? '#0ea5e9'
  const center = pathOffsetX != null ? { centerX: sourceX + pathOffsetX, centerY: (sourceY + targetY) / 2 } : undefined
  const [path, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    borderRadius: 10,
    ...center,
  })
  const markerId = `doc-wf-viz-arrow-${String(id).replace(/[^a-zA-Z0-9_-]/g, '_')}`
  const markerUrl = `url(#${markerId})`
  const pathStyle = {
    ...style,
    stroke,
    strokeWidth: 2,
    fill: 'none',
    strokeDasharray: '8 6',
  } as React.CSSProperties
  return (
    <g className="doc-workflow-viz-edge">
      <defs>
        <marker
          id={markerId}
          markerWidth={10}
          markerHeight={10}
          refX={9}
          refY={5}
          orient="auto"
          markerUnits="userSpaceOnUse"
          viewBox="0 0 10 10"
        >
          <path
            d="M0,0 L0,10 L10,5 Z"
            fill={stroke}
            stroke="rgba(0,0,0,0.15)"
            strokeWidth={0.5}
            strokeLinejoin="round"
          />
        </marker>
      </defs>
      <path
        id={id}
        d={path}
        style={pathStyle}
        markerEnd={markerUrl}
        className="react-flow__edge-path doc-workflow-viz-edge-path-animated"
      />
      {label != null && typeof label === 'string' && label.length > 0 && (
        <g transform={`translate(${labelX}, ${labelY})`} className="react-flow__edge-text">
          <rect
            x={-Math.max(24, (label.length * 6 + 16) / 2)}
            y={-11}
            width={Math.max(48, label.length * 6 + 16)}
            height={22}
            rx={6}
            ry={6}
            fill="#fff"
            stroke={stroke}
            strokeWidth={1.5}
            className="react-flow__edge-textbg"
          />
          <text textAnchor="middle" dominantBaseline="middle" style={{ fontSize: 12, fontWeight: 600, fill: '#1e293b' }}>
            {label}
          </text>
        </g>
      )}
    </g>
  )
}

const edgeTypes = { docWorkflowViz: DocWorkflowVizEdge }

/** Custom end node – više ulaznih handle-a (in-0, in-1, …) da se vide sve veze ka Close workflow */
const DocumentWorkflowEndNode: React.FC<NodeProps<{ label: React.ReactNode; inputCount: number }>> = ({ data }) => {
  const { label, inputCount } = data
  const count = Math.max(1, inputCount)
  return (
    <div className="doc-workflow-viz-node-content doc-workflow-viz-end">
      {Array.from({ length: count }).map((_, i) => (
        <Handle
          key={i}
          type="target"
          position={Position.Top}
          id={`in-${i}`}
          style={{
            left: count === 1 ? '50%' : `${((i + 1) / (count + 1)) * 100}%`,
            transform: 'translateX(-50%)',
          }}
        />
      ))}
      <span className="doc-workflow-viz-end-icon" aria-hidden><i className="bi bi-flag-fill" /></span>
      <div className="doc-workflow-viz-node-title">{label}</div>
    </div>
  )
}

const nodeTypes = { docWorkflowStep: DocumentWorkflowStepNode, docWorkflowEnd: DocumentWorkflowEndNode }

const DocumentWorkflowFlowContent: React.FC = () => {
  const { fitView } = useReactFlow()
  useEffect(() => {
    const t = setTimeout(() => fitView({ padding: 0.28, duration: 300 }), 150)
    return () => clearTimeout(t)
  }, [fitView])

  return (
    <>
      <Background />
      <Controls />
      <MiniMap />
    </>
  )
}

interface DocumentWorkflowDefinitionVisualizationDialogProps {
  /** When provided, fetch definition from API. Omit when using initialDefinition (e.g. preview from edit form). */
  definitionId?: number | null
  definitionName: string
  /** Preview from edit form: use this instead of fetching. Steps should have id = index for correct edge resolution. */
  initialDefinition?: DocumentWorkflowDefinition | null
  /** Status id -> name map for preview (e.g. from edit form workflowStatuses). */
  initialStatusMap?: Record<number, string>
  onClose: () => void
}

const EDGE_STYLE_ID = 'doc-workflow-viz-edge-styles'

const EMPTY_STATUS_MAP: Record<number, string> = {}

function buildStepAssigneeSummary(
  step: DocumentWorkflowDefinitionStep,
  t: (key: string) => string,
  userMap: Record<number, string>
): string {
  const parts: string[] = []
  ;(step.assigned_dynamic_recipients ?? []).forEach((slug) => {
    parts.push(`@${t(`document_workflow.dynamic_recipient_${slug}`)}`)
  })
  ;(step.assigned_user_ids ?? []).forEach((id) => {
    parts.push(userMap[id] ?? `#${id}`)
  })
  return parts.join(', ')
}

export const DocumentWorkflowDefinitionVisualizationDialog: React.FC<DocumentWorkflowDefinitionVisualizationDialogProps> = ({
  definitionId,
  definitionName,
  initialDefinition = null,
  initialStatusMap,
  onClose,
}) => {
  const { t } = useTranslation()
  const effectiveInitialStatusMap = initialStatusMap ?? EMPTY_STATUS_MAP
  const [loading, setLoading] = useState(!initialDefinition)
  const [definition, setDefinition] = useState<DocumentWorkflowDefinition | null>(initialDefinition ?? null)
  const [statusMap, setStatusMap] = useState<Record<number, string>>(effectiveInitialStatusMap)
  const [userMap, setUserMap] = useState<Record<number, string>>({})
  const [fieldMetaById, setFieldMetaById] = useState<Record<number, ConditionFieldMeta>>({})
  const [documentTitleById, setDocumentTitleById] = useState<Record<number, string>>({})

  useEffect(() => {
    const style = document.createElement('style')
    style.id = EDGE_STYLE_ID
    style.textContent = `
      .doc-workflow-viz-flow.react-flow .react-flow__edge path { fill: none !important; stroke-width: 3px !important; }
      .doc-workflow-viz-flow.react-flow .react-flow__edge-path,
      .doc-workflow-viz-flow.react-flow .react-flow__connection-path { fill: none !important; }
      .doc-workflow-viz-flow.react-flow .react-flow__edge marker path { }
    `
    document.head.appendChild(style)
    return () => {
      document.getElementById(EDGE_STYLE_ID)?.remove()
    }
  }, [])

  useEffect(() => {
    if (initialDefinition != null) {
      setDefinition(initialDefinition)
      setStatusMap(effectiveInitialStatusMap)
      setLoading(false)
      return
    }
    if (definitionId == null) {
      setDefinition(null)
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    Promise.all([documentWorkflowDefinitionService.get(definitionId), workflowStatusService.listAll().then((r) => r.results || [])])
      .then(([def, statuses]) => {
        if (cancelled) return
        setDefinition(def)
        const map: Record<number, string> = {}
        ;(statuses as WorkflowStatus[]).forEach((s) => {
          map[s.id] = s.name
        })
        setStatusMap(map)
      })
      .catch(() => {
        if (!cancelled) setDefinition(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [definitionId, initialDefinition, effectiveInitialStatusMap])

  useEffect(() => {
    const hasUserIds = definition?.steps?.some((s) => (s.assigned_user_ids?.length ?? 0) > 0)
    if (!hasUserIds) {
      setUserMap({})
      return
    }
    let cancelled = false
    userService
      .listAll()
      .then((response) => {
        if (cancelled) return
        const map: Record<number, string> = {}
        ;(response.results || []).forEach((user) => {
          map[user.id] = user.username
        })
        setUserMap(map)
      })
      .catch(() => {
        if (!cancelled) setUserMap({})
      })
    return () => {
      cancelled = true
    }
  }, [definition])

  useEffect(() => {
    const hasEntryConditions = definition?.steps?.some((s) => (s.entry_conditions?.length ?? 0) > 0)
    const hasActionBranches = definition?.steps?.some((s) =>
      (s.actions ?? []).some((a) => (a.branches ?? []).length > 0),
    )
    if (!hasEntryConditions && !hasActionBranches) {
      setFieldMetaById({})
      return
    }
    let cancelled = false
    customFieldService
      .listAll()
      .then((response) => {
        if (cancelled) return
        const map: Record<number, ConditionFieldMeta> = {}
        ;(response.results || []).forEach((f: CustomField) => {
          const selectOptions = f.extra_data?.select_options
          const selectOptionLabelById = Array.isArray(selectOptions)
            ? selectOptions.reduce<Record<string, string>>((acc, opt) => {
                if (opt?.id != null) acc[String(opt.id)] = opt.label
                return acc
              }, {})
            : undefined
          map[f.id] = {
            name: f.name,
            data_type: f.data_type,
            selectOptionLabelById,
          }
        })
        setFieldMetaById(map)
      })
      .catch(() => {
        if (!cancelled) setFieldMetaById({})
      })
    return () => {
      cancelled = true
    }
  }, [definition])

  useEffect(() => {
    if (!definition?.steps?.length || !Object.keys(fieldMetaById).length) {
      setDocumentTitleById({})
      return
    }
    const ids = collectDocumentLinkIds(definition.steps, fieldMetaById)
    if (!ids.length) {
      setDocumentTitleById({})
      return
    }
    let cancelled = false
    documentService
      .getFew(ids, { fields: 'id,title' })
      .then((response) => {
        if (cancelled) return
        const map: Record<number, string> = {}
        ;(response.results || []).forEach((d) => {
          map[d.id] = d.title
        })
        setDocumentTitleById(map)
      })
      .catch(() => {
        if (!cancelled) setDocumentTitleById({})
      })
    return () => {
      cancelled = true
    }
  }, [definition, fieldMetaById])

  const steps = useMemo(() => {
    if (!definition?.steps?.length) return []
    return [...definition.steps].sort((a, b) => a.position - b.position)
  }, [definition])

  const initialNodesAndEdges = useMemo(() => {
    const flowNodes: Node[] = []
    const flowEdges: Edge[] = []
    if (!steps.length) return { nodes: flowNodes, edges: flowEdges }

    const centerX = 280
    const startY = 40

    const stepIdToIndex: Record<number, number> = {}
    steps.forEach((s, i) => {
      if (s.id != null) stepIdToIndex[s.id] = i
    })

    const resolveTargetIndex = (
      outcome: { end_workflow: boolean; next_step_id?: number | null; back_to_step_id?: number | null },
      fromIdx: number,
    ): number => {
      if (outcome.end_workflow) return -1
      if (outcome.next_step_id != null && stepIdToIndex[outcome.next_step_id] !== undefined) {
        return stepIdToIndex[outcome.next_step_id]
      }
      if (outcome.back_to_step_id != null && stepIdToIndex[outcome.back_to_step_id] !== undefined) {
        return stepIdToIndex[outcome.back_to_step_id]
      }
      if (fromIdx < steps.length - 1) return fromIdx + 1
      return -1
    }

    const actionOutputs = (
      action: DocumentWorkflowDefinitionStepAction,
      fromIdx: number,
    ): Array<{ targetIdx: number; kind: 'branch' | 'fallback' | 'default'; branch?: DocumentWorkflowDefinitionStepActionBranch }> => {
      const branches = action.branches ?? []
      if (branches.length === 0) {
        return [{ targetIdx: resolveTargetIndex(action, fromIdx), kind: 'default' }]
      }
      const outputs: Array<{ targetIdx: number; kind: 'branch' | 'fallback' | 'default'; branch?: DocumentWorkflowDefinitionStepActionBranch }> =
        branches.map((b) => ({ targetIdx: resolveTargetIndex(b, fromIdx), kind: 'branch', branch: b }))
      outputs.push({ targetIdx: resolveTargetIndex(action, fromIdx), kind: 'fallback' })
      return outputs
    }

    const stepColumnOffset: number[] = new Array(steps.length).fill(0)
    steps.forEach((step: DocumentWorkflowDefinitionStep, fromIdx: number) => {
      const targets = new Set<number>()
      ;(step.actions || []).forEach((a: DocumentWorkflowDefinitionStepAction) => {
        actionOutputs(a, fromIdx).forEach((out) => {
          if (out.targetIdx >= 0) targets.add(out.targetIdx)
        })
      })
      if (targets.size >= 2) {
        const sorted = Array.from(targets).sort((a, b) => a - b)
        sorted.forEach((targetIdx, i) => {
          stepColumnOffset[targetIdx] = i - (sorted.length - 1) / 2
        })
      }
    })

    const hasActionToNext = (step: DocumentWorkflowDefinitionStep, fromIdx: number): boolean => {
      const nextIdx = fromIdx + 1
      if (nextIdx >= steps.length) return false
      return (step.actions || []).some((a) =>
        actionOutputs(a, fromIdx).some((out) => out.targetIdx === nextIdx),
      )
    }

    const countEdgesToEnd = (): number => {
      let n = 0
      steps.forEach((step, fromIdx) => {
        ;(step.actions || []).forEach((a) => {
          actionOutputs(a, fromIdx).forEach((out) => {
            if (out.targetIdx < 0) n++
          })
        })
      })
      return n
    }
    const endIncomingCount = Math.max(1, countEdgesToEnd())

    steps.forEach((step: DocumentWorkflowDefinitionStep, idx: number) => {
      const stepId = `step-${idx}`
      const statusName = statusMap[step.workflow_status_id] ?? `#${step.workflow_status_id}`
      const actionCount = step.actions?.length ?? 0
      const needImplicitNext = actionCount === 0 && idx < steps.length - 1 && !hasActionToNext(step, idx)
      const outputCount = actionCount + (needImplicitNext ? 1 : 0)
      const xOffset = stepColumnOffset[idx] * horizontalSpacing
      const entryConditions = step.entry_conditions ?? []
      const entryConditionCount = entryConditions.length
      const entryConditionSummary = entryConditionCount
        ? entryConditions
            .map((c, i) => `${i + 1}. ${summarizeConditionQuery(c.condition_query || '', fieldMetaById, documentTitleById, t)}`)
            .join('\n')
        : ''
      const entryConditionLabel = entryConditionCount
        ? t('document_workflow.step_entry_conditions_badge', { count: entryConditionCount })
        : ''
      flowNodes.push({
        id: stepId,
        type: 'docWorkflowStep',
        position: { x: centerX - nodeWidth / 2 + xOffset, y: startY + idx * verticalSpacing },
        data: {
          stepLabel: (step.name && step.name.trim()) ? step.name.trim() : `${t('document_workflow.step')} ${idx + 1}`,
          statusName,
          outputCount,
          assigneeSummary: buildStepAssigneeSummary(step, t, userMap) || undefined,
          entryConditionCount,
          entryConditionSummary,
          entryConditionLabel,
        },
        style: {
          borderRadius: 8,
          width: nodeWidth,
          minHeight: nodeHeight,
          background: '#e0f2fe',
          border: '2px solid #0ea5e9',
        },
      })
    })

    const endId = 'end'
    const endY = startY + steps.length * verticalSpacing
    flowNodes.push({
      id: endId,
      type: 'docWorkflowEnd',
      position: { x: centerX - nodeWidth / 2, y: endY },
      data: {
        label: t('document_workflow.action_outcome_end'),
        inputCount: endIncomingCount,
      },
      style: {
        borderRadius: 8,
        width: nodeWidth,
        minHeight: nodeHeight,
        background: '#fecaca',
        border: '2px solid #ef4444',
      },
    })

    let endEdgeIndex = 0
    steps.forEach((step, fromIdx) => {
      const fromId = `step-${fromIdx}`
      const actionCount = step.actions?.length ?? 0
      ;(step.actions || []).forEach((action: DocumentWorkflowDefinitionStepAction, actionIdx) => {
        const outputs = actionOutputs(action, fromIdx)
        const actionLabel = (action.name || t('document_workflow.actions')).trim() || `${t('document_workflow.actions')} ${actionIdx + 1}`
        const strokeColor = getEdgeColorForAction(action.name, actionIdx)

        outputs.forEach((out, outIdx) => {
          const goesToEnd = out.targetIdx < 0
          const targetId = goesToEnd ? endId : `step-${out.targetIdx}`

          let label = actionLabel
          if (out.kind === 'branch' && out.branch) {
            const summary = summarizeConditionQuery(out.branch.condition_query || '', fieldMetaById, documentTitleById, t)
            const ifLabel = t('document_workflow.action_branch_if')
            label = summary ? `${actionLabel} — ${ifLabel} ${summary}` : `${actionLabel} — ${ifLabel}`
          } else if (out.kind === 'fallback') {
            label = `${actionLabel} — ${t('document_workflow.action_branch_otherwise_short')}`
          }

          const isFallback = out.kind === 'fallback'
          const edgeStroke = isFallback ? '#94a3b8' : strokeColor
          const edgeData: { pathOffsetX?: number; stroke: string } = { stroke: edgeStroke }
          if (goesToEnd) {
            edgeData.pathOffsetX = endEdgeIndex % 2 === 0 ? BYPASS_OFFSET : -BYPASS_OFFSET
          }
          const edge: Edge = {
            id: `e-${fromIdx}-${actionIdx}-${outIdx}-${targetId}`,
            source: fromId,
            target: targetId,
            sourceHandle: `out-${actionIdx}`,
            ...(goesToEnd ? { targetHandle: `in-${endEdgeIndex++}` } : { targetHandle: 'in' }),
            data: edgeData,
            label,
            labelStyle: { fontSize: 12, fontWeight: 600, fill: '#1e293b' },
            labelBgStyle: { fill: '#ffffff', fillOpacity: 1, stroke: edgeStroke, strokeWidth: 1.5 },
            labelBgPadding: [6, 4] as [number, number],
            labelBgBorderRadius: 6,
            type: 'docWorkflowViz',
            className: 'doc-workflow-viz-edge',
            style: {
              stroke: edgeStroke,
              strokeWidth: isFallback ? 2 : 3,
              strokeDasharray: isFallback ? '6 4' : undefined,
              zIndex: goesToEnd ? 1 : 2,
            },
          }
          flowEdges.push(edge)
        })
      })

      const needImplicitNext = actionCount === 0 && fromIdx < steps.length - 1 && !hasActionToNext(step, fromIdx)
      if (needImplicitNext) {
        const strokeColor = EDGE_COLORS[0]
        flowEdges.push({
          id: `e-${fromIdx}-impl-next`,
          source: fromId,
          target: `step-${fromIdx + 1}`,
          sourceHandle: 'out-0',
          targetHandle: 'in',
          data: { stroke: strokeColor },
          type: 'docWorkflowViz',
          className: 'doc-workflow-viz-edge',
          style: { stroke: strokeColor, strokeWidth: 3 },
        })
      }
    })

    return { nodes: flowNodes, edges: flowEdges }
  }, [steps, statusMap, userMap, fieldMetaById, documentTitleById, t])

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodesAndEdges.nodes)
  const edges = initialNodesAndEdges.edges
  useEffect(() => {
    setNodes(initialNodesAndEdges.nodes)
  }, [initialNodesAndEdges, setNodes])

  if (loading) {
    return (
      <Modal show onHide={onClose} size="lg" centered>
        <Modal.Body className="text-center py-5">
          <div className="spinner-border text-primary" />
          <p className="mt-2 text-muted">{t('loading_data')}</p>
        </Modal.Body>
      </Modal>
    )
  }

  if (!definition) {
    return (
      <Modal show onHide={onClose} size="lg" centered>
        <Modal.Body className="text-center py-5 text-muted">{t('error_loading_data')}</Modal.Body>
      </Modal>
    )
  }

  return (
    <Modal show onHide={onClose} size="xl" centered className="doc-workflow-definition-viz-dialog" scrollable>
      <Modal.Header closeButton>
        <div>
          <Modal.Title className="mb-1">
            <i className="bi bi-diagram-3 me-2" />
            {t('document_workflow.visualization')}: {definitionName}
          </Modal.Title>
          <p className="doc-workflow-viz-legend text-muted small mb-0">
            {t('document_workflow.viz_legend_steps')} • {t('document_workflow.viz_legend_edges')}
            {nodes.length > 0 && ` • ${t('document_workflow.viz_legend_drag')}`}
          </p>
        </div>
      </Modal.Header>
      <Modal.Body style={{ height: '70vh', minHeight: 420, padding: 0 }}>
        <div className="doc-workflow-viz-container doc-workflow-viz-flow-wrapper">
          <ReactFlowProvider>
            <ReactFlow
              className="doc-workflow-viz-flow"
              nodes={nodes}
              edges={edges}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              fitView
              fitViewOptions={{ padding: 0.28, minZoom: 0.4, maxZoom: 1.2 }}
              nodesDraggable
              onNodesChange={onNodesChange}
              nodesConnectable={false}
              elementsSelectable
              proOptions={{ hideAttribution: true }}
              defaultEdgeOptions={{
                style: { stroke: EDGE_COLORS[0], strokeWidth: 2 },
                type: 'smoothstep',
              }}
            >
              <DocumentWorkflowFlowContent />
            </ReactFlow>
          </ReactFlowProvider>
        </div>
      </Modal.Body>
    </Modal>
  )
}
